import { useEffect, useMemo, useState } from "react";
import { Zap, Target, Flag, Check } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { useAuth } from "@/hooks/useAuth";
import { useGeoCountry } from "@/hooks/useGeoCountry";
import {
  PENDING_XP_AMOUNT,
  readPendingPrediction,
  savePendingPrediction,
  suggestNationFromGeo,
  suggestedNationLabel,
  type GuestPredictionChoice,
} from "@/lib/predictions/guestPrediction";
import { ClaimXpResultScreen } from "@/components/predictions/ClaimXpResultScreen";

/**
 * Prediction-First P0 — Guest micro-prediction card.
 *
 * Lets an unauthenticated visitor answer one of three tap-only options
 * (Gol / Tiro al arco / Córner) and immediately see a visual "+25 XP
 * pendiente" reward, before any signup or NationGate. Persists to
 * `vs_pending_prediction` in localStorage only — does NOT touch Auth,
 * the Prediction Engine, RLS, Realtime or Scoring.
 *
 * Once authenticated the card surfaces a small "ya tienes cuenta" state
 * so we don't compete with the real Prediction Hub.
 */
const OPTIONS: Array<{ id: GuestPredictionChoice; label: string; Icon: typeof Zap }> = [
  { id: "goal", label: "Gol", Icon: Zap },
  { id: "shot", label: "Tiro al arco", Icon: Target },
  { id: "corner", label: "Córner", Icon: Flag },
];

export function GuestMicroPrediction() {
  const { isAuthenticated, authReady } = useAuth();
  const geo = useGeoCountry();
  const suggested = useMemo(
    () => suggestNationFromGeo(geo.country),
    [geo.country],
  );
  const suggestedMeta = suggestedNationLabel(suggested);

  const [choice, setChoice] = useState<GuestPredictionChoice | null>(() => {
    const existing = readPendingPrediction();
    return existing?.choice ?? null;
  });
  const [startedTracked, setStartedTracked] = useState(false);

  useEffect(() => {
    void trackEvent("guest_prediction_started", {
      authenticated: isAuthenticated,
      suggested_nation: suggested,
    });
    setStartedTracked(true);
  }, [isAuthenticated, suggested]);

  useEffect(() => {
    if (choice && startedTracked) {
      void trackEvent("pending_xp_shown", { amount: PENDING_XP_AMOUNT, choice });
    }
  }, [choice, startedTracked]);

  if (!authReady) return null;

  const onPick = (next: GuestPredictionChoice) => {
    setChoice(next);
    savePendingPrediction(next, suggested);
  };

  const completed = choice !== null;

  return (
    <section
      aria-label="Predicción del momento"
      className="overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-background to-accent/10 p-4 shadow-lg"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 items-center rounded-full bg-primary px-2 text-[10px] font-black uppercase tracking-widest text-primary-foreground">
          ⚡ En vivo
        </span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Próxima jugada
        </span>
        {suggestedMeta && (
          <span className="ml-auto text-[11px] font-bold text-muted-foreground">
            {suggestedMeta.flag} {suggestedMeta.name}
          </span>
        )}
      </div>

      <h2 className="mt-2 text-lg font-black leading-tight">
        ¿Qué pasa primero?
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Un tap. Sin registro. Tu predicción suma puntos para tu nación.
      </p>

      <div className="mt-3 grid grid-cols-3 gap-2">
        {OPTIONS.map(({ id, label, Icon }) => {
          const active = choice === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onPick(id)}
              aria-pressed={active}
              className={
                "flex flex-col items-center justify-center gap-1 rounded-xl border px-2 py-3 text-[12px] font-black transition active:scale-95 " +
                (active
                  ? "border-primary bg-primary text-primary-foreground shadow"
                  : "border-border bg-background/60 text-foreground hover:border-primary/60")
              }
            >
              <Icon className="h-4 w-4" />
              {label}
              {active && <Check className="h-3 w-3" />}
            </button>
          );
        })}
      </div>

      {completed && choice && (
        <div className="mt-4">
          <ClaimXpResultScreen
            choice={choice}
            suggestedNation={suggested}
            isAuthenticated={isAuthenticated}
          />
        </div>
      )}
    </section>
  );
}